import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import service from '@/services';
import useWorkspaceStore from '@/stores/WorkspaceStore';
import { SOURCE_TYPE } from '@/types/feedbackConst';

import { getIntegrationQueryKeys } from '../../integrationUtils';
import { type GongInfo, IMPORT_STATE } from '../types';

export const useGong = (enabled: boolean) => {
  const queryClient = useQueryClient();
  const currWorkspace = useWorkspaceStore((state) => state.currWorkspace);
  const workspaceAlias = currWorkspace?.alias;
  if (!workspaceAlias) throw new Error('Workspace alias is required');

  const queryKeys = getIntegrationQueryKeys(SOURCE_TYPE.GONG, workspaceAlias);

  const { data: gongWorkspace, refetch } = useQuery({
    queryKey: queryKeys,
    queryFn: async () => {
      const data = await service.gong.getGongInfo(workspaceAlias);
      if (!data) return null;
      return data as GongInfo;
    },
    enabled,
  });

  const { mutate: disconnectGongMutation } = useMutation({
    mutationFn: () => {
      if (!gongWorkspace) return Promise.resolve(false);
      return service.gong.deleteGongInfo(gongWorkspace.alias);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: queryKeys });
    },
  });

  const { mutate: requestImportMutation } = useMutation({
    mutationFn: (dateRange: NullableDateRangeT) =>
      service.gong.importGongFeedback(
        workspaceAlias,
        dateRange.start_date || '',
        dateRange.end_date || '',
      ),
    onSuccess: () => {
      queryClient.setQueryData(queryKeys, (old: GongInfo | null) => {
        if (!old) return old;
        return {
          ...old,
          import_state: IMPORT_STATE.IMPORTING,
        };
      });
    },
  });

  return {
    gongWorkspace,
    disconnectGongMutation,
    requestImportMutation,
    refetch,
  };
};
